import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, Mail, Phone, MapPin, Linkedin, Twitter, Github } from 'lucide-react';

const footerLinks = {
  sluzby: [
    { label: 'AI Visibility Audit', href: '/audit', isPage: true },
    { label: 'Optimalizace pro AI', href: '/model-spoluprace', isPage: true },
    { label: 'GEO', href: '/model-spoluprace', isPage: true },
    { label: 'Monitoring', href: '/model-spoluprace', isPage: true },
  ],
  zdroje: [
    { label: 'Blog', href: '/blog', isPage: true },
    { label: 'Newsletter', href: '/#newsletter', isPage: false },
    { label: 'FAQ', href: '/#faq', isPage: false },
  ],
  spolecnost: [
    { label: 'O nás', href: '/o-nas', isPage: true },
    { label: 'Kariéra', href: '/kariera', isPage: true },
    { label: 'Kontakt', href: '/o-nas', isPage: true },
  ],
  legal: [
    { label: 'Ochrana osobních údajů', href: '#', isPage: false },
    { label: 'Podmínky použití', href: '#', isPage: false },
    { label: 'Cookies', href: '#', isPage: false },
  ],
};

const socialLinks = [
  { icon: Linkedin, href: '#', label: 'LinkedIn' },
  { icon: Twitter, href: '#', label: 'Twitter' },
  { icon: Github, href: '#', label: 'GitHub' },
];

const Footer = () => {
  const navigate = useNavigate();

  const scrollToSection = (href: string) => {
    if (href.startsWith('/#')) {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(href.replace('/', ''));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const renderLink = (link: { label: string; href: string; isPage: boolean }) => {
    if (link.isPage) {
      return (
        <Link
          to={link.href}
          className="text-slate-500 hover:text-slate-800 transition-colors text-sm"
        >
          {link.label}
        </Link>
      );
    }
    return (
      <button
        onClick={() => scrollToSection(link.href)}
        className="text-slate-500 hover:text-slate-800 transition-colors text-sm text-left"
      >
        {link.label}
      </button>
    );
  };

  return (
    <footer className="relative pt-20 pb-8 px-4 sm:px-6 lg:px-8 border-t border-slate-200/50">
      {/* Background Effect */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#0066FF]/5 rounded-full blur-[200px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6 group">
              <motion.div
                className="relative w-10 h-10 flex items-center justify-center"
                whileHover={{ scale: 1.05, rotate: 5 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#0066FF] to-[#00D4AA] rounded-xl opacity-90 group-hover:opacity-100 transition-opacity" />
                <Zap className="relative w-5 h-5 text-white" />
              </motion.div>
              <span className="text-xl font-bold text-slate-800">
                AI<span className="text-gradient">Visibility</span>
              </span>
            </Link>
            <p className="text-slate-500 mb-6 max-w-sm text-sm leading-relaxed">
              Pomáháme firmám být vidět v AI vyhledávačích. Od auditu přes optimalizaci
              až po pravidelný monitoring změn.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <a
                href="mailto:simonsoucek99@gmail.com"
                className="flex items-center gap-3 text-slate-500 hover:text-slate-800 transition-colors text-sm"
              >
                <Mail className="w-4 h-4" />
                simonsoucek99@gmail.com
              </a>
              <a
                href="tel:+420737509653"
                className="flex items-center gap-3 text-slate-500 hover:text-slate-800 transition-colors text-sm"
              >
                <Phone className="w-4 h-4" />
                +420 737 509 653
              </a>
              <div className="flex items-center gap-3 text-slate-500 text-sm">
                <MapPin className="w-4 h-4" />
                Praha, Česká republika
              </div>
            </div>
          </div>

          {/* Links Columns */}
          <div>
            <h4 className="font-semibold text-slate-800 mb-4 text-sm">Služby</h4>
            <ul className="space-y-3">
              {footerLinks.sluzby.map((link, i) => (
                <li key={i}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-slate-800 mb-4 text-sm">Zdroje</h4>
            <ul className="space-y-3">
              {footerLinks.zdroje.map((link, i) => (
                <li key={i}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-slate-800 mb-4 text-sm">Společnost</h4>
            <ul className="space-y-3">
              {footerLinks.spolecnost.map((link, i) => (
                <li key={i}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-slate-200/50 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-400">
            © {new Date().getFullYear()} AI Visibility. Všechna práva vyhrazena.
          </p>

          {/* Social Links */}
          <div className="flex items-center gap-3">
            {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return (
                <motion.a
                  key={i}
                  href={social.href}
                  aria-label={social.label}
                  className="w-10 h-10 rounded-xl glass-card flex items-center justify-center text-slate-500 hover:text-slate-800 hover:border-[#00D4AA]/30 transition-all"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Icon className="w-4 h-4" />
                </motion.a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
